import { Fragment, useEffect, type ReactNode } from 'react';
import { BookOpen } from 'lucide-react';
import { LoadingState, ErrorState } from '../../components/LoadingState';
import { useGenerateBibliography } from '../../api/queries';
import styles from './BibliographyView.module.css';

type Entry = { citation: string };

// APA citations come back with the title wrapped in *asterisks*, which is
// the canonical mark-up for italics. Convert each *…* span into <em> without
// pulling a full markdown parser (citations don't use any other markdown).
function renderCitation(raw: string): ReactNode {
  const parts = raw.split(/(\*[^*]+\*)/g);
  return parts.map((p, i) =>
    p.startsWith('*') && p.endsWith('*') && p.length > 2 ? (
      <em key={i}>{p.slice(1, -1)}</em>
    ) : (
      <Fragment key={i}>{p}</Fragment>
    )
  );
}

export function BibliographyView() {
  const mut = useGenerateBibliography();
  useEffect(() => {
    if (!mut.data && !mut.isPending && !mut.error) mut.mutate({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mut.isPending) return <LoadingState label="Buscando referencias bibliográficas…" />;
  if (mut.error) return <ErrorState error={mut.error} onRetry={() => mut.mutate({})} />;
  const data = mut.data as any;
  if (!data) return null;

  const list: Entry[] = data.content?.bibliography ?? [];

  if (list.length === 0) {
    return (
      <div className={styles.empty}>
        <BookOpen size={28} />
        <p>No hay referencias bibliográficas para este módulo.</p>
      </div>
    );
  }

  return (
    <div className={styles.view}>
      <p className={styles.intro}>
        {list.length} fuentes citadas en el módulo.
      </p>
      <ol className={styles.list}>
        {list.map((e, i) => (
          <li key={i} className={styles.item}>
            <span className={styles.dot}>{i + 1}</span>
            <span className={styles.cit}>{renderCitation(e.citation)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
